import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Navigation,
  Star
} from "lucide-react";
import { ContactForm } from "@/components/ContactForm";
import { GoogleMap } from "@/components/GoogleMap";
import { SEO } from "@/components/SEO";

const BUSINESS_PHONE = "05 49 05 74 90";
const BUSINESS_PHONE_LINK = "tel:+33549057490";
const BUSINESS_EMAIL = "imediastore79@gmail.com";
const GOOGLE_MAPS_URL = "https://www.google.com/maps/dir//104+Rue+de+la+Gare,+79000+Niort,+France";

const openingHours = [
  { day: "Lundi", hours: "10h00 - 18h00" },
  { day: "Mardi", hours: "10h00 - 18h00" },
  { day: "Mercredi", hours: "10h00 - 18h00" },
  { day: "Jeudi", hours: "10h00 - 18h00" },
  { day: "Vendredi", hours: "10h00 - 18h00" },
  { day: "Samedi", hours: "10h00 - 16h00" },
  { day: "Dimanche", hours: "Fermé" }
];

export default function ContactPage() {
  useEffect(() => {}, []);

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long' });
  const isOpen = today !== 'dimanche';

  return (
    <div className="min-h-screen" data-testid="contact-page">
      <SEO
        title="Contact Mediastore Niort - 104 Rue de la Gare | Réparation Apple, iPhone, Mac"
        description="Contactez Mediastore Niort. ☎ 05 49 05 74 90. 104 Rue de la Gare, 79000 Niort. Spécialiste Apple, réparation iPhone, iPad, MacBook, ordinateur. Diagnostic gratuit, ouvert lun-sam."
        keywords="Mediastore contact Niort, réparation Apple Niort contact, adresse Mediastore, 104 rue de la gare Niort, téléphone Mediastore, horaires Mediastore Niort, réparateur iPhone Niort contact"
        path="/contact"
        breadcrumbs={[
          { name: "Accueil", path: "/" },
          { name: "Contact", path: "/contact" },
        ]}
      />
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-blue-50 to-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Contactez <span className="gradient-text">Mediastore</span>
            </h1>
            <p className="text-lg text-slate-600">
              Une question ? Un devis ? Nous sommes à votre disposition.
              Venez nous voir en boutique ou contactez-nous !
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="text-center card-hover">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Téléphone</h3>
                <a 
                  href={BUSINESS_PHONE_LINK}
                  className="text-xl font-bold text-blue-600 hover:text-blue-700"
                  data-testid="contact-phone"
                >
                  {BUSINESS_PHONE}
                </a>
                <p className="text-sm text-slate-500 mt-2">Lun-Ven 10h-18h</p>
              </CardContent>
            </Card>

            <Card className="text-center card-hover">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Email</h3>
                <a 
                  href={`mailto:${BUSINESS_EMAIL}`}
                  className="text-blue-600 hover:text-blue-700 break-all"
                  data-testid="contact-email"
                >
                  {BUSINESS_EMAIL}
                </a>
                <p className="text-sm text-slate-500 mt-2">Réponse sous 24h</p>
              </CardContent>
            </Card>

            <Card className="text-center card-hover">
              <CardContent className="p-6">
                <div className="w-14 h-14 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-7 w-7 text-blue-600" />
                </div>
                <h3 className="font-semibold text-lg text-slate-900 mb-2">Adresse</h3>
                <p className="text-slate-600">
                  104 Rue de la Gare<br />
                  79000 Niort
                </p>
                <a 
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 mt-2 text-sm"
                  data-testid="contact-directions"
                >
                  <Navigation className="h-4 w-4" />
                  Itinéraire
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <ContactForm />
            </div>

            {/* Info & Map */}
            <div className="space-y-6">
              {/* Opening Hours */}
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Clock className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-slate-900">Horaires d&apos;ouverture</h3>
                      <span className={`text-sm ${isOpen ? 'text-green-600' : 'text-red-600'}`}>
                        {isOpen ? 'Ouvert actuellement' : 'Fermé'}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {openingHours.map((item) => (
                      <div 
                        key={item.day} 
                        className={`flex justify-between py-2 border-b border-slate-100 last:border-0 ${
                          item.day.toLowerCase() === today.toLowerCase() 
                            ? 'font-semibold text-blue-600' 
                            : 'text-slate-600'
                        }`}
                      >
                        <span>{item.day}</span>
                        <span>{item.hours}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Google Rating */}
              <Card className="bg-slate-900 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-slate-400 text-sm mb-1">Note Google</p>
                      <div className="flex items-center gap-2">
                        <span className="text-3xl font-bold">4,6</span>
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star key={`contact-star-${i}`} className={`w-5 h-5 ${i < 4 ? 'text-yellow-400 fill-yellow-400' : 'text-slate-600'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-slate-400 text-sm mt-1">212 avis clients</p>
                    </div>
                    <a 
                      href="https://www.google.com/search?q=mediastore+niort+avis"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Button variant="secondary" size="sm">
                        Voir les avis
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>

              {/* Boutique Photo */}
              <div className="relative rounded-xl overflow-hidden shadow-lg">
                <img 
                  src="https://customer-assets.emergentagent.com/job_b23b68ca-96f4-4732-840a-c86194f2a639/artifacts/5g1iii6g_IMGP9226.jpg"
                  alt="Intérieur de la boutique Mediastore Niort"
                  className="w-full h-[200px] object-cover"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                  <p className="text-white font-semibold">Notre boutique vous accueille</p>
                  <p className="text-white/80 text-sm">104 Rue de la Gare, Niort</p>
                </div>
              </div>

              {/* Map */}
              <div className="h-[300px] rounded-xl overflow-hidden shadow-lg">
                <GoogleMap className="h-full" />
              </div>

              {/* CTA */}
              <div className="flex gap-4">
                <a href={BUSINESS_PHONE_LINK} className="flex-1">
                  <Button 
                    size="lg" 
                    className="w-full bg-blue-600 hover:bg-blue-700 h-12 gap-2"
                    data-testid="contact-call-btn"
                  >
                    <Phone className="h-5 w-5" />
                    Appeler
                  </Button>
                </a>
                <a href={GOOGLE_MAPS_URL} target="_blank" rel="noopener noreferrer" className="flex-1">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="w-full h-12 gap-2"
                    data-testid="contact-map-btn"
                  >
                    <MapPin className="h-5 w-5" />
                    Itinéraire
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
